import React from 'react';

import { Box, BoxProps, Button, Flex, Space, TextInput } from '@mantine/core';
import { UseFormReturnType } from '@mantine/form';
import { useMutation } from '@tanstack/react-query';
import { InputLabelWithInfo } from 'components/InputLabelWithInfo';
import { UpgradePlanBlurCard } from 'components/UpgradePlanBlurCard';
import { useSubscriptionContext } from 'contexts/SubscriptionContext';
import { useTranslation } from 'react-i18next';
import { DiscordService } from 'services/discord-service';
import { NotificationService } from 'services/notification-service';
import { DiscordIntegartionFormValues } from 'shared/interfaces/SmartAssistant';

interface DiscordIntegrationSectionProps extends BoxProps {
  form: UseFormReturnType<DiscordIntegartionFormValues>;
}

export function DiscordIntegrationSection({ form, ...boxProps }: DiscordIntegrationSectionProps) {
  const { t } = useTranslation();

  const [subscription] = useSubscriptionContext();

  // TODO: Decouple discord integration from smartAssistantIntegrations feature flag in product
  const canIntegrate = subscription.product.smartAssistantIntegrations;

  const getDiscordAuthLink = useMutation({
    mutationFn: DiscordService.getAuthLink,
    onSuccess: (link) => {
      window.open(link, '_blank');
      form.setFieldError('discordInviteLink', null);
    },
    onError: () => NotificationService.showResult(false, 'FEED_BACK.SMART_ASSISTANT.FAILURE.AUTHORIZE_DISCORD_SERVER')
  });

  return (
    <UpgradePlanBlurCard
      message={t('SMART_ASSISTANT.DISCORD_INTEGRATION_SECTION.UPGRADE.DESCRIPTION')}
      showCard={!canIntegrate}>
      <Box {...boxProps}>
        <Flex direction="row" align="flex-start" w="100%">
          <TextInput
            w="100%"
            label={
              <InputLabelWithInfo
                label={t('SMART_ASSISTANT.DISCORD.INVITE_LINK.LABEL')}
                infoContentTranslationKey="SMART_ASSISTANT.DISCORD.INVITE_LINK.INFO"
              />
            }
            disabled={!canIntegrate}
            placeholder={t('SMART_ASSISTANT.DISCORD.INVITE_LINK.PLACEHOLDER')}
            {...form.getInputProps('discordInviteLink')}
          />
          <Space w="sm" />
          <Button
            mt={23}
            disabled={!form.values.discordInviteLink.trim() || !canIntegrate}
            loading={getDiscordAuthLink.isLoading}
            onClick={() => {
              form.validateField('discordInviteLink');
              if (form.errors.discordInviteLink || !form.values.discordInviteLink) return;
              getDiscordAuthLink.mutate(form.values.discordInviteLink);
            }}>
            {t('APP.ACTIONS.AUTHORIZE')}
          </Button>
        </Flex>
        <Space h="md" />
        <TextInput
          label={
            <InputLabelWithInfo
              label={t('SMART_ASSISTANT.DISCORD.INVOCATION_CMD.LABEL')}
              infoContentTranslationKey="SMART_ASSISTANT.DISCORD.INVOCATION_CMD.INFO"
            />
          }
          disabled={!canIntegrate}
          placeholder={t('SMART_ASSISTANT.DISCORD.INVOCATION_CMD.PLACEHOLDER')}
          {...form.getInputProps('discordInvocationCommand')}
        />
      </Box>
    </UpgradePlanBlurCard>
  );
}
